import { SkinTone, SkinToneOption, Emoji } from '../types/emoji';

/**
 * Checks if an emoji supports skin tone modification
 * Uses the supportsSkinTone flag from the emoji data
 */
export const supportsSkinTone = (emoji: Emoji): boolean => {
  return emoji.supportsSkinTone === true;
};

/**
 * Applies a skin tone modifier to an emoji character
 * Handles complex emojis with ZWJ sequences (e.g., gendered emojis)
 */
export const applySkinTone = (
  emoji: Emoji,
  skinTone: SkinTone,
  skinToneOptions?: SkinToneOption[]
): string => {
  if (skinTone === 'default' || !supportsSkinTone(emoji) || !skinToneOptions) {
    return emoji.char;
  }

  const skinToneOption = skinToneOptions.find((option) => option.id === skinTone);
  if (!skinToneOption?.modifier) {
    return emoji.char;
  }

  const ZWJ = '\u200D'; // Zero Width Joiner
  const VARIATION_SELECTOR = '\uFE0F';

  // Convert string to array of codepoints to handle surrogate pairs correctly
  const chars = Array.from(emoji.char);

  // Find the first ZWJ to determine where to insert skin tone
  const zwjIndex = chars.findIndex((char) => char === ZWJ);

  if (zwjIndex > 0) {
    // Complex emoji with ZWJ sequence (e.g., gendered emoji)
    // Insert skin tone after base emoji, before ZWJ sequence
    const baseEmoji = chars[0];
    const restOfSequence = chars.slice(zwjIndex).filter((char) => char !== VARIATION_SELECTOR); // Remove variation selectors

    return baseEmoji + skinToneOption.modifier + restOfSequence.join('');
  } else {
    // Simple emoji or emoji without ZWJ
    // Just add skin tone after base emoji, removing variation selectors
    const cleanedEmoji = chars.filter((char) => char !== VARIATION_SELECTOR).join('');

    return cleanedEmoji + skinToneOption.modifier;
  }
};

/**
 * Gets the skin tone modifier for a specific skin tone
 */
export const getSkinToneModifier = (
  skinTone: SkinTone,
  skinToneOptions?: SkinToneOption[]
): string | undefined => {
  if (skinTone === 'default' || !skinToneOptions) {
    return undefined;
  }

  const skinToneOption = skinToneOptions.find((option) => option.id === skinTone);
  return skinToneOption?.modifier;
};

/**
 * Gets the skin tone from localStorage
 */
export const getStoredSkinTone = (): SkinTone => {
  try {
    const stored = localStorage.getItem('emoji-skin-tone');
    if (
      stored &&
      ['default', 'light', 'medium-light', 'medium', 'medium-dark', 'dark'].includes(stored)
    ) {
      return stored as SkinTone;
    }
  } catch (error) {
    console.warn('Failed to load skin tone from localStorage:', error);
  }
  return 'default';
};

/**
 * Stores the skin tone preference in localStorage
 */
export const storeSkinTone = (skinTone: SkinTone): void => {
  try {
    localStorage.setItem('emoji-skin-tone', skinTone);
  } catch (error) {
    console.warn('Failed to store skin tone in localStorage:', error);
  }
};
